import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';
import type { QuestionInputField, QuestionSchemaType, emissionDropdown } from '/index';

export default function useFuelsSupplierSchema({
  buildingFuelTypes: fuelTypes,
}: {
  buildingFuelTypes: emissionDropdown[];
}): QuestionSchemaType {
  const { t } = useTranslation();
  const [valueFuelType, setValueFuelType] = useState<string | null>(null);

  const fuelType: QuestionInputField = useMemo(() => {
    const fuelTypeValues: {
      displayName?: string;
      displayNameKey: string;
      isPlaceholder?: boolean;
      name: string | null;
    }[] = [
      {
        name: null,
        isPlaceholder: true,
        displayNameKey: 'business:QuestionPage.forms.otherFuels.selectFuel',
      },
      ...fuelTypes.map(({ displayName, name }) => {
        return {
          name,
          displayName,
          displayNameKey: 'business:emissionFactors.buildingFuelTypes.'.concat(displayName),
        };
      }),
    ];
    return {
      propName: 'fuelType',
      label: 'Emission source:',
      type: FIELD_TYPE_DROPDOWN,
      values: fuelTypeValues,
      saveMeCallback: (value) => {
        const { inputValue: fuelTypeSlug } = value as { inputValue: string };
        return {
          fuelType: fuelTypes.find(({ name }) => name === fuelTypeSlug),
          hasFuelType: true,
        };
      },
      onChangeCallback: (value: string) => {
        setValueFuelType(value);
      },
      element: 'fuel_type',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.fuelType.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRFUELTYPENOTFOUND',
          errStr: t('business:Errors.fuelType.notFound'),
        },
      ],
    };
  }, [fuelTypes, t]);

  const quantity: QuestionInputField = useMemo(() => {
    const fuelTypesUnits = fuelTypes.map((item: emissionDropdown) => {
      const typeUnitMapped = item?.units?.map((typeUnit) => {
        if (typeof typeUnit === 'string' || typeof typeUnit === 'number') {
          const transLitDisplay = t('business:emissionFactors.volumes.'.concat(String(typeUnit)), String(typeUnit));
          return {
            name: typeUnit,
            displayNameKey: transLitDisplay,
          };
        }
        return { name: typeUnit.name, displayNameKey: typeUnit.displayName };
      });
      return {
        ...item,
        units: typeUnitMapped,
      };
    });
    return {
      element: 'quantity',
      propName: 'quantity',
      type: FIELD_TYPE_NUMBER,
      styleInputMaxWidth: '20rem',
      dropdown: {
        values: fuelTypesUnits?.find(({ name }: { name: string | null }) => name === valueFuelType)?.units,
        propName: 'fuelUnit',
        element: 'fuel_unit',
      },
      label: 'Quantity:',
      placeholderI18nKey: 'business:QuestionPage.forms.otherFuels.quantityPlaceholder',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOQUANTITY',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRQUANTITYTOOLOW',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: 0,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRQUANTITYTOOHIGH',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: 999999,
        },
      ],
    };
  }, [fuelTypes, valueFuelType, t]);

  const dataSource: QuestionInputField = useMemo(() => {
    return {
      element: 'data_source',
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      labelI18nKey: 'business:QuestionPage.forms.otherFuels.para5',
      descriptionI18nKey: 'business:QuestionPage.forms.otherFuels.para6',
      placeholderI18nKey: 'business:QuestionPage.forms.otherFuels.dataPlaceholder',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.provide'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.provide'),
          value: 3000,
        },
      ],
    };
  }, [t]);

  const pageSchema: QuestionInputField[] = useMemo(
    () => [fuelType, quantity, dataSource],
    [fuelType, quantity, dataSource],
  );
  return {
    pageSchema,
  };
}
